import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';

export interface LanguageExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  isDefault?: boolean;
  translationStatus?: LanguageTranslationStatus;
  metadata?: Record<string, unknown>;
}

export interface LanguageFormInput {
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
  metadata: Record<string, string | number | boolean>;
}

export type LanguageTranslationStatus = 'ready' | 'translating' | 'failed' | 'pending';

export const LANGUAGE_FIELD_LABELS: Record<string, string> = {
  code: 'Language',
  name: 'Language name',
  nativeName: 'Native name',
  locale: 'Locale',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const LANGUAGE_MAX_SORT_ORDER = 999;

export const TRANSLATION_STATUS_META: Record<LanguageTranslationStatus, { label: string; color: 'success' | 'info' | 'error' | 'default'; description: string }> = {
  ready: { label: 'Ready', color: 'success', description: 'Every admin and mobile string is translated.' },
  translating: { label: 'Translating…', color: 'info', description: 'The auto-translate job is running. Untranslated strings show in English until it finishes.' },
  failed: { label: 'Needs attention', color: 'error', description: 'Some strings are still in English. Run Re-translate or fix them in Translator Master — missing strings fall back to English.' },
  pending: { label: 'Not translated', color: 'default', description: 'No translations yet — users who pick this language see English text.' },
};

const LOCALE_PATTERN = /^([a-z]{2,3})(?:-[A-Z][a-z]{3})?(?:-(?:[A-Z]{2}|\d{3}))?$/;
const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M} .'()-]*$/u;

const str = (v: unknown) => (v == null ? '' : String(v).trim());
export const normalizeLanguageName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

export function translationStatusOf(meta: Record<string, unknown> = {}): LanguageTranslationStatus {
  if (meta.provisioning) return 'translating';
  if (meta.provisionError) return 'failed';
  if (meta.provisioned) return 'ready';
  return 'pending';
}

/** Turns "hi_in" / "HI-in" into "hi-IN" while typing. */
export function normalizeLocaleInput(raw: string): string {
  const parts = raw.trim().replace(/_/g, '-').split('-');
  return parts
    .map((p, i) => {
      if (i === 0) return p.toLowerCase();
      if (/^[A-Za-z]{4}$/.test(p)) return p[0].toUpperCase() + p.slice(1).toLowerCase();
      if (/^[A-Za-z]{2}$/.test(p)) return p.toUpperCase();
      return p;
    })
    .join('-');
}

export function localeError(locale: string, code: string): string | undefined {
  if (!locale) return `Locale is required, e.g. ${code ? `${code}-XX` : 'en-US'}`;
  const match = LOCALE_PATTERN.exec(locale);
  if (!match) return 'Use BCP 47: language, optional script, region — e.g. en-US, zh-Hant-TW, es-419';
  if (code && match[1] !== code) return `Must start with the language code "${code}"`;
  try {
    if (Intl.getCanonicalLocales(locale)[0] !== locale) return `"${locale}" is not a valid locale`;
  } catch {
    return `"${locale}" is not a valid locale`;
  }
  return undefined;
}

export function nextLanguageSortOrder(existing: LanguageExistingRecord[], editingId?: string) {
  const used = existing.filter((r) => r._id !== editingId).map((r) => Number(r.sortOrder) || 0);
  return Math.max(0, ...used) + 1;
}

function repeatedTextError(label: string, text: string) {
  const run = findConsecutiveRepeatedCharacter(text);
  if (run) return `${label} cannot repeat the same character 4+ times ("${run.slice(0, 4)}")`;
  const word = findRepeatedWord(text);
  if (word) return `${label} cannot repeat the same word back to back ("${word}")`;
  return undefined;
}

/** Field errors keyed by field name (metadata keys are flattened). */
export function validateLanguageForm(
  form: LanguageFormInput,
  ctx: { existing: LanguageExistingRecord[]; editingId?: string; knownCode: boolean },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const code = str(form.code).toLowerCase();
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);

  if (!/^[a-z]{2,3}$/.test(code) || !ctx.knownCode) errors.code = 'Select a language from the ISO 639-1 list';
  else if (others.some((r) => str(r.code).toLowerCase() === code)) errors.code = `"${code}" is already added`;

  const name = normalizeLanguageName(form.name);
  if (!name) errors.name = 'Language name is required';
  else if (name.length < 2 || name.length > 60) errors.name = 'Language name must be 2–60 characters';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Start with a letter; use letters, spaces or . \' ( ) -';
  else {
    const repeat = repeatedTextError('Language name', name);
    if (repeat) errors.name = repeat;
    else {
      const clash = others.find((r) => normalizeLanguageName(r.name).toLowerCase() === name.toLowerCase());
      if (clash) errors.name = `"${name}" already exists (code ${clash.code})`;
    }
  }

  const nativeName = normalizeLanguageName(form.metadata.nativeName);
  if (!nativeName) errors.nativeName = 'Native name is required — the language name written in that language';
  else if (nativeName.length > 60) errors.nativeName = 'At most 60 characters';
  else {
    const repeat = repeatedTextError('Native name', nativeName);
    if (repeat) errors.nativeName = repeat;
  }

  const locale = str(form.metadata.locale);
  const locErr = localeError(locale, code);
  if (locErr) errors.locale = locErr;
  else {
    const clash = others.find((r) => str(r.metadata?.locale).toLowerCase() === locale.toLowerCase());
    if (clash) errors.locale = `${locale} is already used by ${clash.name}`;
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > LANGUAGE_MAX_SORT_ORDER) {
    errors.sortOrder = `Enter a whole number between 1 and ${LANGUAGE_MAX_SORT_ORDER}`;
  } else {
    const clash = others.find((r) => Number(r.sortOrder) === order);
    if (clash) {
      errors.sortOrder = `Sort order ${order} is already used by ${clash.name} — next available is ${nextLanguageSortOrder(ctx.existing, ctx.editingId)}`;
    }
  }

  const description = str(form.description);
  if (description.length > 500) errors.description = 'Description must be at most 500 characters';
  else if (description) {
    const repeat = repeatedTextError('Description', description);
    if (repeat) errors.description = repeat;
  }
  return errors;
}
